import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ApiService from '../../../../services/ApiService';
import ConfirmationModal from '../../../../components/ModalConfirmation';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import CertificateModal from './CertificateModal';
import NumberingModal from './NumberingModal';
import {
  AMBIENTE_LABEL,
  AcaoPendencia,
  SITUACAO_LABEL,
  certificadoChip,
  certificadoUtilizavel,
  emissorTitulo,
  extractApiError,
  formatCnpj,
  formatDate,
  pendenciaDoEmissor,
} from './helpers';
import {
  Banner,
  Card,
  Chip,
  FieldError,
  FieldLabel,
  MutedText,
  PillButton,
  SectionTitle,
  TextInput,
} from './index.style';
import {
  CardHeader,
  CertBox,
  CertName,
  EnvRow,
  FormGrid,
  HeaderChips,
  HeaderRow,
  LinkButton,
  NumEmpty,
  NumGatewayBox,
  NumHead,
  NumLabel,
  NumTable,
  NumValue,
  Segmented,
  SegmentButton,
  Sheet,
  Subtitle,
  Switch,
  Title,
  ToggleRow,
} from './EmitterSheet.style';
import { AmbienteEmissao, EmissorPainel, ProducaoNoGateway, UpdateEmissorPayload } from './types';

const AMBIENTES: AmbienteEmissao[] = ['HOMOLOGACAO', 'PRODUCAO'];

const fichaSchema = z.object({
  razaoSocial: z.string().trim(),
  nomeFantasia: z.string().trim(),
  email: z.union([z.literal(''), z.string().trim().email('E-mail inválido.')]),
  telefoneComercial: z.string().trim(),
  enviarPorEmail: z.boolean(),
  ambienteEmissao: z.enum(['HOMOLOGACAO', 'PRODUCAO']),
});

type FichaForm = z.infer<typeof fichaSchema>;

const valoresDaFicha = (emissor: EmissorPainel): FichaForm => ({
  razaoSocial: emissor.dados?.razaoSocial ?? '',
  nomeFantasia: emissor.dados?.nomeFantasia ?? '',
  email: emissor.dados?.email ?? '',
  telefoneComercial: emissor.dados?.telefoneComercial ?? '',
  enviarPorEmail: emissor.enviarPorEmail,
  ambienteEmissao: emissor.ambienteEmissao,
});

type Confirmacao = 'producao' | 'desativar' | 'reativar';

const CONFIRMACOES: Record<Confirmacao, { title: string; message: string; label: string; variant: 'primary' | 'danger' }> = {
  producao: {
    title: 'Passar para produção?',
    message:
      'As próximas notas deste emissor passam a ter valor fiscal quando o servidor estiver em produção. Confirme para salvar.',
    label: 'Salvar em produção',
    variant: 'primary',
  },
  desativar: {
    title: 'Desativar emissor?',
    message:
      'Ele deixa de aparecer para emitir notas novas. As notas já emitidas continuam consultáveis e canceláveis, e o emissor pode ser reativado com a mesma numeração.',
    label: 'Desativar',
    variant: 'danger',
  },
  reativar: {
    title: 'Reativar emissor?',
    message: 'Ele volta a aparecer para emitir notas, com a mesma numeração.',
    label: 'Reativar',
    variant: 'primary',
  },
};

interface EmitterSheetProps {
  emissor: EmissorPainel;
  ambienteServidor: AmbienteEmissao;
  gatewayDisponivel: boolean;
  isAdmin: boolean;
  onUpdated: (emissor: EmissorPainel) => void;
  onDirtyChange: (dirty: boolean) => void;
  onControles?: (controles: ControlesFicha) => void;
}

export interface ControlesFicha {
  salvar: () => void;
  descartar: () => void;
  salvando: boolean;
}

const EmitterSheet = ({ emissor, ambienteServidor, gatewayDisponivel, isAdmin, onUpdated, onDirtyChange, onControles }: EmitterSheetProps) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const [certificadoAberto, setCertificadoAberto] = useState(false);
  const [reativarAposCertificado, setReativarAposCertificado] = useState(false);
  const [numeracaoAberta, setNumeracaoAberta] = useState<AmbienteEmissao | null>(null);
  const [confirmandoGateway, setConfirmandoGateway] = useState(false);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<FichaForm>({ resolver: zodResolver(fichaSchema), defaultValues: valoresDaFicha(emissor) });

  useEffect(() => {
    reset(valoresDaFicha(emissor), { keepDirtyValues: true });
  }, [emissor, reset]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const ambienteEscolhido = watch('ambienteEmissao');
  const enviarPorEmail = watch('enviarPorEmail');
  const titulo = emissorTitulo(emissor);
  const situacao = SITUACAO_LABEL[emissor.situacao];
  const cert = certificadoChip(emissor.certificado.estado, emissor.certificado.diasParaVencer);
  const dadosDisponiveis = emissor.dados !== null;

  const numeracaoPorAmbiente = useMemo(
    () => new Map(emissor.numeracao.map((n) => [n.ambiente, n])),
    [emissor.numeracao],
  );

  const aviso = (message: string, statusOk = true) => setFeedback({ isOpen: true, message, statusOk });

  const aplicar = async (payload: UpdateEmissorPayload, mensagem: string, limparFicha = false) => {
    setSalvando(true);
    setErro(null);
    try {
      const { data } = await ApiService.fiscal.updateEmissor(emissor.id, payload);
      if (limparFicha) reset(valoresDaFicha(data));
      onUpdated(data);
      aviso(mensagem);
    } catch (error) {
      setErro(extractApiError(error, 'Não foi possível salvar.').message);
    } finally {
      setSalvando(false);
    }
  };

  const salvar = handleSubmit(async (values) => {
    const payload: UpdateEmissorPayload = {};
    if (dirtyFields.razaoSocial) payload.razaoSocial = values.razaoSocial;
    if (dirtyFields.nomeFantasia) payload.nomeFantasia = values.nomeFantasia;
    if (dirtyFields.email) payload.email = values.email;
    if (dirtyFields.telefoneComercial) payload.telefoneComercial = values.telefoneComercial;
    if (dirtyFields.enviarPorEmail) payload.enviarPorEmail = values.enviarPorEmail;
    if (dirtyFields.ambienteEmissao) payload.ambienteEmissao = values.ambienteEmissao;
    await aplicar(payload, 'Alterações salvas.', true);
  });

  const onSalvarClick = () => {
    if (dirtyFields.ambienteEmissao && ambienteEscolhido === 'PRODUCAO') setConfirmacao('producao');
    else salvar();
  };

  const acoesFicha = useRef({ salvar: onSalvarClick, descartar: () => reset(valoresDaFicha(emissor)) });
  acoesFicha.current = { salvar: onSalvarClick, descartar: () => reset(valoresDaFicha(emissor)) };

  useEffect(() => {
    onControles?.({
      salvar: () => acoesFicha.current.salvar(),
      descartar: () => acoesFicha.current.descartar(),
      salvando,
    });
  }, [onControles, salvando]);

  const onReativarClick = () => {
    if (certificadoUtilizavel(emissor.certificado.estado)) setConfirmacao('reativar');
    else {
      setReativarAposCertificado(true);
      setCertificadoAberto(true);
    }
  };

  const onCertificadoSalvo = async (atualizado: EmissorPainel) => {
    setCertificadoAberto(false);
    const valido = certificadoUtilizavel(atualizado.certificado.estado);
    if (reativarAposCertificado && valido) {
      setReativarAposCertificado(false);
      onUpdated(atualizado);
      await aplicar({ ativo: true }, 'Certificado carregado e emissor reativado.');
      return;
    }
    setReativarAposCertificado(false);
    onUpdated(atualizado);
    aviso(valido ? 'Certificado carregado.' : 'Certificado carregado, mas ele não está válido.', valido);
  };

  const pendencia = pendenciaDoEmissor(emissor);
  const acoesPendencia: Record<AcaoPendencia, () => void> = {
    certificado: () => setCertificadoAberto(true),
    reativar: onReativarClick,
  };

  const usarNumeracaoDoGateway = async (producao: ProducaoNoGateway) => {
    setConfirmandoGateway(false);
    setSalvando(true);
    setErro(null);
    try {
      const { data } = await ApiService.fiscal.configurarNumeracaoEmissor(emissor.id, {
        ambiente: 'PRODUCAO',
        serieNFe: producao.serie,
        sequencialNFe: producao.proximoNumero,
        sequencialLoteNFe: producao.loteInicial,
      });
      onUpdated(data);
      aviso('Numeração configurada.');
    } catch (error) {
      setErro(extractApiError(error, 'Não foi possível configurar a numeração.').message);
    } finally {
      setSalvando(false);
    }
  };

  const confirmar = (tipo: Confirmacao) => {
    setConfirmacao(null);
    if (tipo === 'producao') salvar();
    else if (tipo === 'desativar') aplicar({ ativo: false }, 'Emissor desativado.');
    else aplicar({ ativo: true }, 'Emissor reativado.');
  };

  return (
    <Sheet>
      {erro && <Banner $tone='bad'>{erro}</Banner>}
      <Card>
        <HeaderRow>
          <div>
            <Title>{titulo}</Title>
            <Subtitle>CNPJ {formatCnpj(emissor.empresa.cnpj)}</Subtitle>
          </div>
          <HeaderChips>
            <Chip $tone={situacao.tone}>{situacao.label}</Chip>
            <Chip $tone='info'>Emite em {AMBIENTE_LABEL[emissor.ambienteEmissao].toLowerCase()}</Chip>
          </HeaderChips>
        </HeaderRow>
        {pendencia && (
          <Banner $tone={pendencia.tone}>
            <span>{pendencia.texto}</span>
            <PillButton type='button' $variant='primary' onClick={acoesPendencia[pendencia.acao]} disabled={salvando}>
              {pendencia.rotulo}
            </PillButton>
          </Banner>
        )}
      </Card>

      <Card>
        <CardHeader>
          <SectionTitle>Certificado digital A1</SectionTitle>
          <PillButton type='button' onClick={() => setCertificadoAberto(true)}>
            {emissor.certificado.estado === 'AUSENTE' ? 'Carregar certificado' : 'Substituir certificado'}
          </PillButton>
        </CardHeader>
        {emissor.certificado.estado === 'AUSENTE' ? (
          <MutedText>Nenhum certificado carregado. Sem ele o emissor fica pendente e não emite.</MutedText>
        ) : (
          <CertBox $tone={cert.tone === 'ok' ? 'ok' : cert.tone === 'warn' ? 'warn' : 'bad'}>
            <div>
              <CertName>{emissor.certificado.nome ?? 'Certificado sem titular informado'}</CertName>
              <MutedText>Válido até {formatDate(emissor.certificado.vencimento)}</MutedText>
            </div>
            <Chip $tone={cert.tone}>{cert.label}</Chip>
          </CertBox>
        )}
      </Card>

      {isAdmin && (
        <Card $dashed>
          <EnvRow>
            <div>
              <SectionTitle>Ambiente de emissão</SectionTitle>
              <MutedText>
                Produção exige certificado válido e numeração de produção.
                {ambienteServidor === 'HOMOLOGACAO' && ' Este servidor emite só em homologação.'}
              </MutedText>
            </div>
            <input type='hidden' {...register('ambienteEmissao')} />
            <Segmented>
              {AMBIENTES.map((ambiente) => (
                <SegmentButton
                  key={ambiente}
                  type='button'
                  $active={ambienteEscolhido === ambiente}
                  disabled={ambiente === 'PRODUCAO' && !emissor.podeProducao && emissor.ambienteEmissao !== 'PRODUCAO'}
                  onClick={() => setValue('ambienteEmissao', ambiente, { shouldDirty: true })}
                >
                  {AMBIENTE_LABEL[ambiente]}
                </SegmentButton>
              ))}
            </Segmented>
          </EnvRow>
        </Card>
      )}

      <Card>
        <SectionTitle>Numeração da NF-e</SectionTitle>
        <NumTable>
          <span />
          <NumHead>Série</NumHead>
          <NumHead>Lote inicial</NumHead>
          <NumHead>Próximo número</NumHead>
          {AMBIENTES.map((ambiente) => {
            const numeracao = numeracaoPorAmbiente.get(ambiente);
            const podeConfigurar = ambiente === 'HOMOLOGACAO' || isAdmin;
            return (
              <React.Fragment key={ambiente}>
                <NumLabel>{AMBIENTE_LABEL[ambiente]}</NumLabel>
                {numeracao ? (
                  <>
                    <NumValue>{numeracao.serie ?? '—'}</NumValue>
                    <NumValue>{numeracao.loteInicial ?? '—'}</NumValue>
                    <NumValue>{numeracao.proximoNumero ?? '—'}</NumValue>
                  </>
                ) : ambiente === 'PRODUCAO' && emissor.producaoNoGateway ? (
                  <NumGatewayBox>
                    <span>
                      Numeração já definida no Nota Gateway: série {emissor.producaoNoGateway.serie}, próximo nº{' '}
                      {emissor.producaoNoGateway.proximoNumero}.
                    </span>
                    {podeConfigurar && (
                      <PillButton type='button' onClick={() => setConfirmandoGateway(true)} disabled={salvando}>
                        Usar esta numeração
                      </PillButton>
                    )}
                  </NumGatewayBox>
                ) : (
                  <NumEmpty>
                    Sem numeração de {AMBIENTE_LABEL[ambiente].toLowerCase()}.
                    {podeConfigurar && (
                      <PillButton
                        type='button'
                        onClick={() => setNumeracaoAberta(ambiente)}
                        disabled={ambiente === 'PRODUCAO' && !gatewayDisponivel}
                      >
                        {ambiente === 'PRODUCAO' && !gatewayDisponivel
                          ? 'Aguardando o Nota Gateway'
                          : `Configurar ${AMBIENTE_LABEL[ambiente].toLowerCase()}`}
                      </PillButton>
                    )}
                  </NumEmpty>
                )}
              </React.Fragment>
            );
          })}
        </NumTable>
        <MutedText>A partir do número inicial, a sequência é controlada pelo Nota Gateway.</MutedText>
      </Card>

      <Card>
        <CardHeader>
          <SectionTitle>Dados do emissor</SectionTitle>
          <LinkButton type='button' onClick={() => navigate(`/panel/companies/edit/?id=${emissor.companyId}`)}>
            Abrir cadastro da empresa
          </LinkButton>
        </CardHeader>
        {!dadosDisponiveis && (
          <MutedText>Nota Gateway indisponível: os dados do emissor não puderam ser carregados agora.</MutedText>
        )}
        <FormGrid>
          <FieldLabel>
            Razão social
            <TextInput disabled={!dadosDisponiveis} {...register('razaoSocial')} />
          </FieldLabel>
          <FieldLabel>
            Nome fantasia
            <TextInput disabled={!dadosDisponiveis} {...register('nomeFantasia')} />
          </FieldLabel>
          <FieldLabel>
            E-mail fiscal
            <TextInput type='email' disabled={!dadosDisponiveis} {...register('email')} />
            {errors.email && <FieldError>{errors.email.message}</FieldError>}
          </FieldLabel>
          <FieldLabel>
            Telefone comercial
            <TextInput disabled={!dadosDisponiveis} {...register('telefoneComercial')} />
          </FieldLabel>
          <FieldLabel>
            CNPJ
            <TextInput readOnly value={formatCnpj(emissor.empresa.cnpj)} />
          </FieldLabel>
          <FieldLabel>
            Inscrição estadual
            <TextInput readOnly value={emissor.empresa.ie ?? '—'} />
          </FieldLabel>
        </FormGrid>
        <MutedText>
          Estes dados são do emissor no Nota Gateway e não alteram o cadastro da empresa. CNPJ, IE e endereço vêm do
          cadastro da empresa{emissor.empresa.cidade ? ` (${emissor.empresa.cidade}/${emissor.empresa.uf ?? ''})` : ''}.
        </MutedText>
      </Card>

      <Card>
        <ToggleRow>
          Enviar NF-e por e-mail ao destinatário
          <Switch $on={enviarPorEmail}>
            <input type='checkbox' {...register('enviarPorEmail')} />
          </Switch>
        </ToggleRow>
        <EnvRow>
          <MutedText>{emissor.ativo ? 'Emissor ativo' : 'Emissor inativo'}</MutedText>
          {emissor.ativo ? (
            <PillButton type='button' $variant='danger' onClick={() => setConfirmacao('desativar')} disabled={salvando}>
              Desativar emissor
            </PillButton>
          ) : (
            <PillButton type='button' onClick={onReativarClick} disabled={salvando}>
              Reativar emissor
            </PillButton>
          )}
        </EnvRow>
      </Card>

      {certificadoAberto && (
        <CertificateModal
          emissor={emissor}
          titulo={titulo}
          onClose={() => {
            setCertificadoAberto(false);
            setReativarAposCertificado(false);
          }}
          onSaved={onCertificadoSalvo}
        />
      )}

      {numeracaoAberta && (
        <NumberingModal
          emissor={emissor}
          ambiente={numeracaoAberta}
          onClose={() => setNumeracaoAberta(null)}
          onSaved={(atualizado) => {
            setNumeracaoAberta(null);
            onUpdated(atualizado);
            aviso('Numeração configurada.');
          }}
        />
      )}

      {confirmandoGateway && emissor.producaoNoGateway && (
        <ConfirmationModal
          isOpen
          title='Usar a numeração do Nota Gateway?'
          message={`A numeração de produção deste emissor será criada com série ${emissor.producaoNoGateway.serie} e próximo número ${emissor.producaoNoGateway.proximoNumero}, iguais às do Nota Gateway. Nada é alterado no Nota Gateway.`}
          actions={[
            { label: 'Cancelar', variant: 'outline', onClick: () => setConfirmandoGateway(false) },
            {
              label: 'Usar esta numeração',
              variant: 'primary',
              onClick: () => {
                if (emissor.producaoNoGateway) usarNumeracaoDoGateway(emissor.producaoNoGateway);
              },
            },
          ]}
        />
      )}

      {confirmacao && (
        <ConfirmationModal
          isOpen
          title={CONFIRMACOES[confirmacao].title}
          message={CONFIRMACOES[confirmacao].message}
          actions={[
            { label: 'Cancelar', variant: 'outline', onClick: () => setConfirmacao(null) },
            {
              label: CONFIRMACOES[confirmacao].label,
              variant: CONFIRMACOES[confirmacao].variant,
              onClick: () => confirmar(confirmacao),
            },
          ]}
        />
      )}
    </Sheet>
  );
};

export default EmitterSheet;
